import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faEraser,
  faExpand,
  faMinus,
  faPlus,
  faTv,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { BeamerScreen } from "../beamer/BeamerScreen";
import { useFullscreen } from "../beamer/useFullscreen";
import { useSwipe } from "./useSwipe";
import { CyCarousel } from "./CyCarousel";
import { CyBeamer } from "../beamer/CyBeamer";

interface Props {
  previousSong: () => void;
  nextSong: () => void;
  previousLyricsPart: () => void;
  nextLyricsPart: () => void;
  emptyLyricsScreen: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  openBeamerWindow: () => void;
}

interface PadButtonProps {
  area: string;
  icon: IconDefinition;
  label: string;
  onClick: () => void;
  /** smaller icon for the less important font size buttons */
  minor?: boolean;
  "data-cy"?: string;
}

function PadButton({ area, icon, label, minor, ...rest }: PadButtonProps) {
  return (
    <Button
      color="secondary"
      outline
      className={
        "beamer-pad-button" + (minor ? " beamer-pad-button-minor" : "")
      }
      style={{ gridArea: area }}
      aria-label={label}
      title={label}
      {...rest}
    >
      <FontAwesomeIcon icon={icon} fixedWidth />
    </Button>
  );
}

/**
 * Preview and remote control of the beamer, shown next to the lyrics in beamer mode.
 * Placement (aside on the right or dock at the bottom) is set by CSS, see .beamer-panels.
 */
export function BeamerPanels(props: Props) {
  const { t } = useTranslation();
  const fullscreen = useFullscreen();
  //content moves with the finger: swiping left shows the next song, up the next lyrics part
  const swipeHandlers = useSwipe({
    onSwipeLeft: props.nextSong,
    onSwipeRight: props.previousSong,
    onSwipeUp: props.nextLyricsPart,
    onSwipeDown: props.previousLyricsPart,
  });

  return (
    <aside className="beamer-panels">
      <section className="beamer-panel beamer-preview-panel">
        <h2 className="beamer-panel-title">
          {t("carousel.beamerPanel.preview")}
        </h2>
        <div className="beamer-preview">
          <BeamerScreen
            fullscreen={fullscreen}
            swipeHandlers={swipeHandlers}
            data-cy={CyBeamer.LyricsContent}
          />
        </div>
        <div className="beamer-output">
          <Button
            color="primary"
            outline
            onClick={fullscreen.toggleFullscreen}
            aria-label={t("carousel.beamerPanel.maximize")}
            title={t("carousel.beamerPanel.maximize")}
            data-cy={CyCarousel.MaximizeBeamerButton}
          >
            <FontAwesomeIcon icon={faExpand} fixedWidth />
            <span className="beamer-output-label ms-2">
              {t("carousel.beamerPanel.maximize")}
            </span>
          </Button>
          <Button
            color="primary"
            outline
            onClick={props.openBeamerWindow}
            aria-label={t("carousel.beamerPanel.externalWindow")}
            title={t("carousel.beamerPanel.externalWindow")}
          >
            <FontAwesomeIcon icon={faTv} fixedWidth />
            <span className="beamer-output-label ms-2">
              {t("carousel.beamerPanel.externalWindow")}
            </span>
          </Button>
        </div>
      </section>
      <section className="beamer-panel beamer-control-panel">
        <h2 className="beamer-panel-title">
          {t("carousel.beamerPanel.control")}
        </h2>
        <div className="beamer-pad">
          <PadButton
            area="smaller"
            minor
            icon={faMinus}
            label={t("carousel.beamerPanel.smallerFont")}
            onClick={props.decreaseFontSize}
            data-cy={CyCarousel.DecreaseFontButton}
          />
          <PadButton
            area="up"
            icon={faChevronUp}
            label={t("carousel.beamerPanel.previousLyricsPart")}
            onClick={props.previousLyricsPart}
            data-cy={CyCarousel.PreviousLyricsPartButton}
          />
          <PadButton
            area="larger"
            minor
            icon={faPlus}
            label={t("carousel.beamerPanel.largerFont")}
            onClick={props.increaseFontSize}
            data-cy={CyCarousel.IncreaseFontButton}
          />
          <PadButton
            area="left"
            icon={faChevronLeft}
            label={t("carousel.beamerPanel.previousSong")}
            onClick={props.previousSong}
            data-cy={CyCarousel.PreviousItem}
          />
          <PadButton
            area="clear"
            icon={faEraser}
            label={t("carousel.beamerPanel.emptyScreen")}
            onClick={props.emptyLyricsScreen}
            data-cy={CyCarousel.SendEmptyScreenCommandButton}
          />
          <PadButton
            area="right"
            icon={faChevronRight}
            label={t("carousel.beamerPanel.nextSong")}
            onClick={props.nextSong}
            data-cy={CyCarousel.NextItem}
          />
          <PadButton
            area="down"
            icon={faChevronDown}
            label={t("carousel.beamerPanel.nextLyricsPart")}
            onClick={props.nextLyricsPart}
            data-cy={CyCarousel.NextLyricsPartButton}
          />
        </div>
      </section>
    </aside>
  );
}
